import { useState, useEffect, useCallback, useRef } from 'react';
import { Notification, NotificationService } from '../services/NotificationService';
import { NotificationApi } from '../services/NotificationApi';
import { getServerUrl } from '../utils/server-config';

interface UseNotificationsConfig {
    getToken: () => string | null;
    autoConnect?: boolean;
    maxNotifications?: number;
}

interface UseNotificationsReturn {
    notifications: Notification[];
    unreadCount: number;
    isConnected: boolean;
    isLoading: boolean;
    error: string | null;
    // Actions
    markAsRead: (notificationId: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    refetchNotifications: () => Promise<void>;
    connectWebSocket: () => Promise<void>;
    disconnectWebSocket: () => void;
}

export const useNotifications = ({
    getToken,
    autoConnect = true,
    maxNotifications = 50
}: UseNotificationsConfig): UseNotificationsReturn => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const notificationServiceRef = useRef<NotificationService | null>(null);
    const notificationApiRef = useRef<NotificationApi | null>(null);
    const mountedRef = useRef(true);
    const serverUrl = getServerUrl();

    // Initialize API client
    useEffect(() => {
        notificationApiRef.current = NotificationApi.create(getToken);
    }, [getToken]);

    // Load initial notifications
    const loadNotifications = useCallback(async () => {
        if (!notificationApiRef.current) return;

        const token = getToken();

        if (!token) {
            const errorMessage = 'No authentication token available. Please log in.';
            console.warn('[useNotifications]', errorMessage);
            setError(errorMessage);
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            const [notificationsResponse, unreadCountResponse] = await Promise.all([
                notificationApiRef.current.getNotifications(1, maxNotifications),
                notificationApiRef.current.getUnreadCount()
            ]);

            if (mountedRef.current) {
                setNotifications(notificationsResponse.data);
                setUnreadCount(unreadCountResponse);
            }
        } catch (err) {
            console.error('[useNotifications] Failed to load notifications:', err);
            if (mountedRef.current) {
                setError(err instanceof Error ? err.message : 'Failed to load notifications');
            }
        } finally {
            if (mountedRef.current) {
                setIsLoading(false);
            }
        }
    }, [maxNotifications, getToken]);

    // Connect to WebSocket
    const connectWebSocket = useCallback(async () => {
        const token = getToken();

        if (!token) {
            const errorMessage = 'No authentication token available for WebSocket connection';
            setError(errorMessage);
            return;
        }

        try {
            // Don't reconnect if already connected
            if (notificationServiceRef.current?.isConnected()) {
                return;
            }

            // Disconnect existing connection
            if (notificationServiceRef.current) {
                notificationServiceRef.current.disconnect();
            }

            const service = new NotificationService({
                serverUrl,
                token,
                onNotification: (notification) => {
                    if (mountedRef.current) {
                        // Use functional update to ensure we always work with the latest state
                        setNotifications(prev => {
                            // Check if notification already exists (prevent duplicates)
                            const exists = prev.some(n => n.id === notification.id);
                            if (exists) {
                                return prev;
                            }

                            // Add new notification at the beginning
                            const updated = [notification, ...prev];
                            // Keep only maxNotifications items
                            const result = updated.slice(0, maxNotifications);
                            return result;
                        });

                        // Only increment unread count if notification is unread
                        if (!notification.isRead) {
                            setUnreadCount(prev => prev + 1);
                        }
                    }
                },
                onUnreadCount: (count) => {
                    if (mountedRef.current) {
                        setUnreadCount(count);
                    }
                },
                onConnectionChange: (connected) => {
                    if (mountedRef.current) {
                        setIsConnected(connected);
                        if (connected) {
                            setError(null);
                        }
                    }
                },
                onError: (err) => {
                    console.error('[useNotifications] WebSocket error:', err);
                    if (mountedRef.current) {
                        setError(err instanceof Error ? err.message : 'WebSocket connection error');
                        setIsConnected(false);
                    }
                }
            });

            notificationServiceRef.current = service;
            await service.connect();

            // Get initial unread count via WebSocket
            setTimeout(() => {
                if (service.isConnected()) {
                    service.getUnreadCount();
                }
            }, 1000);

        } catch (err) {
            console.error('[useNotifications] Failed to connect WebSocket:', err);
            if (mountedRef.current) {
                setError(err instanceof Error ? err.message : 'Failed to connect to real-time notifications');
                setIsConnected(false);
            }
        }
    }, [serverUrl, getToken, maxNotifications]);

    // Disconnect WebSocket
    const disconnectWebSocket = useCallback(() => {
        if (notificationServiceRef.current) {
            notificationServiceRef.current.disconnect();
            notificationServiceRef.current = null;
        }
        setIsConnected(false);
    }, []);

    // Mark notification as read
    const markAsRead = useCallback(async (notificationId: string) => {
        try {
            // Optimistically update UI
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));

            // Update via WebSocket if connected
            if (notificationServiceRef.current?.isConnected()) {
                notificationServiceRef.current.markAsRead(notificationId);
            }

            // Backup: Update via REST API
            if (notificationApiRef.current) {
                await notificationApiRef.current.markAsRead(notificationId);
            }

        } catch (err) {
            console.error('[useNotifications] Failed to mark as read:', err);
            // Revert optimistic update
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, isRead: false } : n)
            );
            setUnreadCount(prev => prev + 1);
            throw err;
        }
    }, []);

    // Mark all notifications as read
    const markAllAsRead = useCallback(async () => {
        try {
            // Optimistically update UI
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            setUnreadCount(0);

            // Update via WebSocket if connected
            if (notificationServiceRef.current?.isConnected()) {
                notificationServiceRef.current.markAllAsRead();
            }

            // Backup: Update via REST API
            if (notificationApiRef.current) {
                await notificationApiRef.current.markAllAsRead();
            }

        } catch (err) {
            console.error('[useNotifications] Failed to mark all as read:', err);
            // Reload notifications to get correct state
            await loadNotifications();
            throw err;
        }
    }, [loadNotifications]);

    // Initialize on mount
    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            disconnectWebSocket();
        };
    }, []); // Empty dependency array - run only once on mount

    // Load notifications when component mounts or token changes
    useEffect(() => {
        loadNotifications();
    }, [loadNotifications]);

    // Connect WebSocket when component mounts or autoConnect changes
    // Use ref to track if WebSocket has been initialized to prevent multiple connections
    const webSocketInitializedRef = useRef(false);

    useEffect(() => {
        if (autoConnect && !webSocketInitializedRef.current) {
            webSocketInitializedRef.current = true;
            connectWebSocket();
        }

        return () => {
            // Only reset on unmount
            webSocketInitializedRef.current = false;
        };
    }, [autoConnect, connectWebSocket]);

    return {
        notifications,
        unreadCount,
        isConnected,
        isLoading,
        error,
        markAsRead,
        markAllAsRead,
        refetchNotifications: loadNotifications,
        connectWebSocket,
        disconnectWebSocket
    };
};